export const isExternalLink = (url?: string): boolean => url?.startsWith('http');

// need to make sure relative links start with a slash if they don't already have one
export const getRelativeLink = (url?: string): string =>
  !url?.startsWith('/') && !url?.startsWith('http') ? `/${url}` : url;

export const getRandomNegativeNumber = (): number =>
  Math.floor(getSecuritySensitiveRandomNumber() * (100000 - 1 + 1) + 1) * -1;

export const getSecuritySensitiveRandomNumber = (): number => {
  const crypto = window.crypto;
  const array = new Uint32Array(1);
  const randomValue = crypto.getRandomValues(array)[0]; // Compliant for security-sensitive use cases
  return randomValue / 0x100000000; //between 0 and 1
};

export const generatePassword = (): string => {
  let length = 10;
  let string = 'abcdefghijklmnopqrstuvwxyz';
  let numeric = '0123456789';
  let punctuation = '!@#$%^&*()_+~`|}{[]:;?,./-=';
  let password = '';
  let character = '';
  while (password.length < length) {
    let entity1 = Math.ceil(
      string.length * getSecuritySensitiveRandomNumber() * getSecuritySensitiveRandomNumber()
    );
    let entity2 = Math.ceil(
      numeric.length * getSecuritySensitiveRandomNumber() * getSecuritySensitiveRandomNumber()
    );
    let entity3 = Math.ceil(
      punctuation.length * getSecuritySensitiveRandomNumber() * getSecuritySensitiveRandomNumber()
    );
    let hold1 = string.charAt(entity1);
    let hold = password.length % 2 === 0 ? hold1.toUpperCase() : hold1;
    character += hold;
    character += numeric.charAt(entity2);
    character += punctuation.charAt(entity3);
    password = character;
  }
  password = password
    .split('')
    .sort(function () {
      return 0.5 - getSecuritySensitiveRandomNumber();
    })
    .join('');
  return password.substring(0, length);
};

export const alphaSort = (arr: any[], key: string) => {
  return arr.sort((a, b) => {
    const nameA = a[key]?.toUpperCase(); // ignore upper and lowercase
    const nameB = b[key]?.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1; //nameA comes first
    }
    if (nameA > nameB) {
      return 1; // nameB comes first
    }
    return 0; // names must be equal
  });
};

export const dateSortDesc = (arr: any[], key: string) => {
  return arr.sort((a, b) => {
    const nameA = new Date(a[key]); // convert to date
    const nameB = new Date(b[key]); // convert to date
    if (nameA > nameB) {
      return -1; //nameA comes first
    }
    if (nameA < nameB) {
      return 1; // nameB comes first
    }
    return 0; // names must be equal
  });
};

export const numberSortAsc = (arr: any[], key: string) => {
  return arr.sort((a, b) => {
    const itemA = a[key];
    const itemB = b[key];
    return itemA - itemB;
  });
};

export const getNameInitials = (name: string) => {
  // name includes a space return just for the first letter
  if (name.indexOf(' ') === -1) {
    return `${name.split(' ')[0][0]}`.toUpperCase();
  }
  // else return the first letter of first name and first letter of last name
  return `${name.split(' ')[0][0].toUpperCase()}${name.split(' ')[1][0].toUpperCase()}`;
};

/**
 * HTML generated by Quill when the WYSIWYG editor is "blank"
 */
const emptyWysiwygHtml: string[] = [
  '<h1><br></h1>',
  '<h2><br></h2>',
  '<h3><br></h3>',
  '<h4><br></h4>',
  '<h5><br></h5>',
  '<h6><br></h6>',
  '<p><br></p>',
  '<h1> </h1>',
  '<h2> </h2>',
  '<h3> </h3>',
  '<h4> </h4>',
  '<h5> </h5>',
  '<h6> </h6>',
  '<p> </p>',
  '<h1></h1>',
  '<h2></h2>',
  '<h3></h3>',
  '<h4></h4>',
  '<h5></h5>',
  '<h6></h6>',
  '<p></p>'
];

/**
 * Check if Quill WYISWYG editor content is actually empty
 * @param content string | undefined
 * @returns boolean
 */
export const isWysiwygEmpty = (content?: string | null): boolean => {
  let empty = true;

  if (content) {
    content = content.trim();

    const html = emptyWysiwygHtml.find(emptyHtml => emptyHtml === content);
    if (!html) {
      empty = false;
    }
  }

  return empty;
};

export const truncString = (str: string, max: number) => {
  return typeof str === 'string' && str.length > max ? `${str.substring(0, max)}...` : str;
};

/**
 *
 * @param path string the key of the object you are trying to resolve the value of
 * @param obj the object that gets passed in to check against
 * @returns the value of the object at the given path, i.e. path='test.test.value' obj={{ test: { test: { value: 'TEST' } }  }}
 */
export const resolveObjectField = (path: string, obj: any) => {
  return path.split('.').reduce((prev, curr) => {
    return prev ? prev[curr] : null;
  }, obj);
};

export const groupBy = (xs: any[], key: string) => {
  return xs.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export const downloadCSV = (csv: string, fileName: string) => {
  const BOM = String.fromCharCode(0xfeff);
  // Create an file the user can download
  const el = document.createElement('a');
  el.href = `data:text/csv;charset=UTF-8,${encodeURI(`${BOM}${csv}`)}`;
  el.target = '_blank';
  el.download = `${fileName}.csv`;
  // Download the file
  el.click();
};
export const customStringify = obj => {
  const replacer = [];
  for (const key in obj) {
    replacer.push(key);
  }
  return JSON.stringify(obj, replacer);
};

export const handleStateUpdateWithoutSave = (
  shouldWarn: boolean,
  warningMessage: string = 'You have unsaved changes, are you sure you want to exit?',
  action?: () => void
) => {
  if (shouldWarn) {
    const result = window.confirm(warningMessage);
    if (result) {
      action?.();
    } else {
      return;
    }
  } else {
    action?.();
  }
};

export const XOR = (a: boolean, b: boolean) => {
  return (a || b) && !(a && b);
};

export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.tiff'];

export const ALLOWED_VIDEO_EXTENSIONS = ['.mp4', '.mov'];

export const isVideoFile = (fileName: string) => {
  const fileExt = fileName.split('.').pop();
  return ALLOWED_VIDEO_EXTENSIONS.includes(fileExt ?? '');
};

export const SALES_PORTAL_PER_PAGE_OPTIONS = [25, 50, 100, 250, 500];

export const DEFAULT_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

enum SortDirection {
  Asc = 'Asc',
  Desc = 'Desc',
  None = ''
}

export const handleSortDirection = (column: SortDirection) => {
  // Order of sorting is Asc -> Desc -> None
  switch (column) {
    case SortDirection.Asc:
      return SortDirection.Desc;
    case SortDirection.Desc:
      return SortDirection.None;
    case SortDirection.None:
    default:
      return SortDirection.Asc;
  }
};

export const dangerousCSVStartCharacters = ['=', '-', '+', '@'];
/**
 * Escape characters for exporting to csv
 * @param cell string
 * @returns string
 */
export function sanitizeCsvString(cell: string | null | undefined): string {
  // check if string is empty, null, or undefined
  if (cell) {
    // escape double quotes
    if (cell.indexOf('"') !== -1) {
      cell = cell.replace(/"/g, '""');
    }
    // escape commas
    if (cell.includes(',')) {
      cell = cell.replace(/,/g, ' ');
    }
    // replace new line with space
    if (cell.indexOf('\n') !== -1) {
      cell = cell.replace(/\n/g, ' ');
    }
    // replace new line with space
    if (cell.indexOf('\r') !== -1) {
      cell = cell.replace(/\r/g, ' ');
    }
    if (cell.startsWith('=')) {
      cell = cell.replace('=', `'=`);
    }
    if (cell.startsWith('+')) {
      cell = cell.replace('+', `'+`);
    }
    if (cell.startsWith('-')) {
      cell = cell.replace('-', `'-`);
    }
    if (cell.startsWith('@')) {
      cell = cell.replace('@', `'@`);
    }
    return cell;
  } else {
    return '';
  }
}

export const convertToNumber = (input: number | string | null | undefined): number => {
  if (!input) {
    return 0;
  }

  if (typeof input === 'number') {
    return input;
  }

  if (typeof input === 'string') {
    const value = parseFloat(input.replace(/[^0-9.-]+/g, ''));
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }

  return 0;
};

export const copyTextToClipboard = async (text: string) => {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand('copy', true, text);
  }
};

export const stripHtmlTags = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const objectToQueryString = (obj: Record<string, any>): string => {
  if (!obj) return '';
  return Object.entries(obj)
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      }
      if (value !== undefined && value !== null && value !== '') {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
      return [];
    })
    .join('&');
};

export const hyphenSeparateTwoInputs = (a?: string, b?: string): string => {
  return `${a ? a : ''}${a && b ? ' - ' : ''}${b ? b : ''}`;
};
